import { FC, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Save, Cancel } from "@mui/icons-material";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import ControlledSelectField from "../../components/controlledComponents/ControlledSelectField";
import {
  useAddMaterialItemHook,
  useUpdateMaterialItemHook,
} from "../../../hooks/useMaterialHook";
import { logger } from "../../../utils/common";
import {
  formatLabel,
  materialItemTypeOptions,
  materialStatusOptions,
} from "./material.helpers";
import {
  TMaterialItem,
  TMaterialItemType,
  TMaterialStatus,
} from "../../../apis/material/material.interfaces";

const itemSchema = z.object({
  item_type: z.string().min(1, "Type is required"),
  name: z
    .string()
    .min(1, "Name is required")
    .max(200, "Name must not exceed 200 characters"),
  description: z.string().max(2000).optional().or(z.literal("")),
  quantity: z
    .union([z.string(), z.number()])
    .transform((v) => {
      const n = typeof v === "number" ? v : Number(v);
      return Number.isFinite(n) ? n : NaN;
    })
    .refine((n) => Number.isFinite(n) && n >= 0, "Quantity must be ≥ 0"),
  status: z.string().min(1, "Status is required"),
  // Books
  isbn: z.string().max(40).optional().or(z.literal("")),
  author: z.string().max(200).optional().or(z.literal("")),
  publisher: z.string().max(200).optional().or(z.literal("")),
  edition: z.string().max(50).optional().or(z.literal("")),
  subject: z.string().max(100).optional().or(z.literal("")),
  grade_level: z.string().max(50).optional().or(z.literal("")),
  language: z.string().max(50).optional().or(z.literal("")),
  // Equipment
  equipment_type: z.string().max(100).optional().or(z.literal("")),
  model_number: z.string().max(100).optional().or(z.literal("")),
  manufacturer: z.string().max(200).optional().or(z.literal("")),
  serial_number: z.string().max(100).optional().or(z.literal("")),
  // Digital resource
  url: z.string().max(500).optional().or(z.literal("")),
  resource_type: z.string().max(100).optional().or(z.literal("")),
  format: z.string().max(50).optional().or(z.literal("")),
  // Stationery
  stationery_type: z.string().max(100).optional().or(z.literal("")),
  brand: z.string().max(100).optional().or(z.literal("")),
});

type ItemFormData = z.input<typeof itemSchema>;

interface ManageMaterialItemDialogProps {
  open: boolean;
  onClose: () => void;
  materialGroupId: string;
  item?: TMaterialItem | null;
}

const ManageMaterialItemDialog: FC<ManageMaterialItemDialogProps> = ({
  open,
  onClose,
  materialGroupId,
  item,
}) => {
  const isEdit = !!item;
  const addMutation = useAddMaterialItemHook();
  const updateMutation = useUpdateMaterialItemHook();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ItemFormData>({
    resolver: zodResolver(itemSchema),
    defaultValues: {
      item_type: "book",
      name: "",
      description: "",
      quantity: 1,
      status: "available",
      isbn: "",
      author: "",
      publisher: "",
      edition: "",
      subject: "",
      grade_level: "",
      language: "",
      equipment_type: "",
      model_number: "",
      manufacturer: "",
      serial_number: "",
      url: "",
      resource_type: "",
      format: "",
      stationery_type: "",
      brand: "",
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        item_type: (item?.item_type as string) ?? "book",
        name: item?.name ?? "",
        description: item?.description ?? "",
        quantity: item?.quantity ?? 1,
        status: (item?.status as string) ?? "available",
        isbn: item?.isbn ?? "",
        author: item?.author ?? "",
        publisher: item?.publisher ?? "",
        edition: item?.edition ?? "",
        subject: item?.subject ?? "",
        grade_level: item?.grade_level ?? "",
        language: item?.language ?? "",
        equipment_type: item?.equipment_type ?? "",
        model_number: item?.model_number ?? "",
        manufacturer: item?.manufacturer ?? "",
        serial_number: item?.serial_number ?? "",
        url: item?.url ?? item?.digital_url ?? "",
        resource_type: item?.resource_type ?? "",
        format: item?.format ?? item?.digital_format ?? "",
        stationery_type: item?.stationery_type ?? "",
        brand: item?.brand ?? "",
      });
    }
  }, [open, item, reset]);

  const itemType = watch("item_type");

  const onSubmit = async (data: ItemFormData) => {
    const parsed = itemSchema.parse(data);

    const stripBlank = <T extends string | undefined>(v: T): string | undefined =>
      v && v.length > 0 ? v : undefined;

    const itemPayload: TMaterialItem = {
      item_type: parsed.item_type as TMaterialItemType,
      name: parsed.name,
      description: stripBlank(parsed.description),
      quantity: parsed.quantity,
      status: parsed.status as TMaterialStatus,
      isbn: stripBlank(parsed.isbn),
      author: stripBlank(parsed.author),
      publisher: stripBlank(parsed.publisher),
      edition: stripBlank(parsed.edition),
      subject: stripBlank(parsed.subject),
      grade_level: stripBlank(parsed.grade_level),
      language: stripBlank(parsed.language),
      equipment_type: stripBlank(parsed.equipment_type),
      model_number: stripBlank(parsed.model_number),
      manufacturer: stripBlank(parsed.manufacturer),
      serial_number: stripBlank(parsed.serial_number),
      url: stripBlank(parsed.url),
      resource_type: stripBlank(parsed.resource_type),
      format: stripBlank(parsed.format),
      stationery_type: stripBlank(parsed.stationery_type),
      brand: stripBlank(parsed.brand),
    };

    try {
      if (isEdit && item?.id) {
        await updateMutation.mutateAsync({
          materialGroupId,
          itemId: item.id,
          payload: { item: itemPayload },
        });
        logger.show_success("Item updated successfully!");
      } else {
        await addMutation.mutateAsync({
          materialGroupId,
          payload: { item: itemPayload },
        });
        logger.show_success("Item added successfully!");
      }
      onClose();
    } catch (error) {
      logger.show_error(
        error instanceof Error
          ? error.message
          : "Failed to save item. Please try again.",
      );
    }
  };

  const itemTypeOptions = materialItemTypeOptions.map((t) => ({
    key: t,
    value: formatLabel(t),
  }));
  const statusOptions = materialStatusOptions.map((s) => ({
    key: s,
    value: formatLabel(s),
  }));

  const showBookFields = itemType === "book";
  const showEquipmentFields = itemType === "equipment";
  const showDigitalFields = itemType === "digital_resource";
  const showStationeryFields = itemType === "stationery";

  const activeMutation = isEdit ? updateMutation : addMutation;
  const isLoading = isSubmitting || activeMutation.isPending;
  const isError = activeMutation.isError;
  const errorMessage = activeMutation.error?.message;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-testid="manage-material-item-dialog"
    >
      <DialogTitle data-testid="manage-material-item-dialog-title">
        {isEdit ? "Edit Item" : "Add Item"}
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)} data-testid="material-item-form">
        <DialogContent data-testid="material-item-form-content">
          {isError && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              data-testid="material-item-form-error"
            >
              {errorMessage || "Failed to save item. Please try again."}
            </Alert>
          )}

          <Grid container spacing={3} sx={{ mt: 1 }}>
            <ControlledSelectField
              control={control}
              name="item_type"
              id="material-item-type"
              label="Type"
              isRequired
              itemList={itemTypeOptions}
              errorMessage={errors.item_type?.message}
            />
            <ControlledSelectField
              control={control}
              name="status"
              id="material-item-status"
              label="Status"
              isRequired
              itemList={statusOptions}
              errorMessage={errors.status?.message}
            />
            <ControlledTextField
              control={control}
              name="name"
              id="material-item-name"
              label="Name"
              required
              errorMessage={errors.name?.message}
              inputProps={{ "data-testid": "material-item-name-input" }}
            />
            <ControlledTextField
              control={control}
              name="quantity"
              id="material-item-quantity"
              label="Quantity"
              type="number"
              required
              errorMessage={errors.quantity?.message as string | undefined}
              inputProps={{ "data-testid": "material-item-quantity-input" }}
            />
            <ControlledTextField
              control={control}
              name="description"
              id="material-item-description"
              label="Description"
              multiline
              rows={2}
              errorMessage={errors.description?.message}
              inputProps={{ "data-testid": "material-item-description-input" }}
            />

            {showBookFields && (
              <>
                <ControlledTextField
                  control={control}
                  name="isbn"
                  id="material-item-isbn"
                  label="ISBN"
                  errorMessage={errors.isbn?.message}
                />
                <ControlledTextField
                  control={control}
                  name="author"
                  id="material-item-author"
                  label="Author"
                  errorMessage={errors.author?.message}
                />
                <ControlledTextField
                  control={control}
                  name="publisher"
                  id="material-item-publisher"
                  label="Publisher"
                  errorMessage={errors.publisher?.message}
                />
                <ControlledTextField
                  control={control}
                  name="edition"
                  id="material-item-edition"
                  label="Edition"
                  errorMessage={errors.edition?.message}
                />
                <ControlledTextField
                  control={control}
                  name="subject"
                  id="material-item-subject"
                  label="Subject"
                  errorMessage={errors.subject?.message}
                />
                <ControlledTextField
                  control={control}
                  name="grade_level"
                  id="material-item-grade-level"
                  label="Grade Level"
                  errorMessage={errors.grade_level?.message}
                />
                <ControlledTextField
                  control={control}
                  name="language"
                  id="material-item-language"
                  label="Language"
                  errorMessage={errors.language?.message}
                />
              </>
            )}

            {showEquipmentFields && (
              <>
                <ControlledTextField
                  control={control}
                  name="equipment_type"
                  id="material-item-equipment-type"
                  label="Equipment Type"
                  errorMessage={errors.equipment_type?.message}
                />
                <ControlledTextField
                  control={control}
                  name="model_number"
                  id="material-item-model-number"
                  label="Model Number"
                  errorMessage={errors.model_number?.message}
                />
                <ControlledTextField
                  control={control}
                  name="manufacturer"
                  id="material-item-manufacturer"
                  label="Manufacturer"
                  errorMessage={errors.manufacturer?.message}
                />
                <ControlledTextField
                  control={control}
                  name="serial_number"
                  id="material-item-serial-number"
                  label="Serial Number"
                  errorMessage={errors.serial_number?.message}
                />
              </>
            )}

            {showDigitalFields && (
              <>
                <ControlledTextField
                  control={control}
                  name="resource_type"
                  id="material-item-resource-type"
                  label="Resource Type"
                  errorMessage={errors.resource_type?.message}
                />
                <ControlledTextField
                  control={control}
                  name="format"
                  id="material-item-format"
                  label="Format"
                  errorMessage={errors.format?.message}
                />
                <ControlledTextField
                  control={control}
                  name="url"
                  id="material-item-url"
                  label="URL"
                  errorMessage={errors.url?.message}
                  inputProps={{ "data-testid": "material-item-url-input" }}
                />
              </>
            )}

            {showStationeryFields && (
              <>
                <ControlledTextField
                  control={control}
                  name="stationery_type"
                  id="material-item-stationery-type"
                  label="Stationery Type"
                  errorMessage={errors.stationery_type?.message}
                />
                <ControlledTextField
                  control={control}
                  name="brand"
                  id="material-item-brand"
                  label="Brand"
                  errorMessage={errors.brand?.message}
                />
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onClose}
            disabled={isLoading}
            startIcon={<Cancel />}
            data-testid="cancel-material-item-button"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : <Save />}
            data-testid="save-material-item-button"
          >
            {isLoading
              ? isEdit
                ? "Saving..."
                : "Adding..."
              : isEdit
                ? "Save Item"
                : "Add Item"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ManageMaterialItemDialog;
